import { useState, useEffect, useRef, useCallback } from 'react'

const WS_URL = import.meta.env.VITE_WS_URL || 'wss://api.lynkstar.app/ws'

export interface WSMessage {
  type: string
  payload: Record<string, unknown>
}

interface UseWebSocketReturn {
  isConnected: boolean
  lastMessage: WSMessage | null
  sendMessage: (message: WSMessage) => void
  sendEncryptedMessage: (recipientId: string, ciphertext: string) => void
  sendTyping: (recipientId: string) => void
  sendReadReceipt: (recipientId: string, messageIds: string[]) => void
  sendRTCOffer: (targetId: string, sdp: string, callType?: 'audio' | 'video') => void
  sendRTCAnswer: (targetId: string, sdp: string) => void
  sendICECandidate: (targetId: string, candidate: RTCIceCandidate) => void
  disconnect: () => void
}

const MAX_RECONNECT_ATTEMPTS = 5
const PING_INTERVAL = 30000 // 30 seconds

export function useWebSocket(authToken: string | null): UseWebSocketReturn {
  const [isConnected, setIsConnected] = useState(false)
  const [lastMessage, setLastMessage] = useState<WSMessage | null>(null)

  const wsRef = useRef<WebSocket | null>(null)
  const reconnectAttemptsRef = useRef(0)
  const reconnectTimerRef = useRef<number | null>(null)
  const pingTimerRef = useRef<number | null>(null)

  const connect = useCallback(() => {
    if (!authToken) return

    try {
      const url = `${WS_URL}?token=${encodeURIComponent(authToken)}`
      wsRef.current = new WebSocket(url)

      wsRef.current.onopen = () => {
        console.debug('[WebSocket] Connected')
        setIsConnected(true)
        reconnectAttemptsRef.current = 0

        // Start ping timer
        pingTimerRef.current = window.setInterval(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'ping', payload: {} }))
          }
        }, PING_INTERVAL)
      }

      wsRef.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data) as WSMessage
          console.debug('[WebSocket] Received message:', message.type, message.payload)
          setLastMessage(message)
        } catch (err) {
          console.error('[WebSocket] Failed to parse message:', err)
        }
      }

      wsRef.current.onerror = (error) => {
        console.error('[WebSocket] Error:', error)
      }

      wsRef.current.onclose = () => {
        console.debug('[WebSocket] Disconnected')
        setIsConnected(false)
        scheduleReconnect()
      }
    } catch (error) {
      console.error('[WebSocket] Connection failed:', error)
      scheduleReconnect()
    }
  }, [authToken])

  const scheduleReconnect = useCallback(() => {
    if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
      console.debug('[WebSocket] Max reconnect attempts reached')
      return
    }

    const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000)
    reconnectAttemptsRef.current++

    console.debug(`[WebSocket] Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current})`)

    reconnectTimerRef.current = window.setTimeout(() => {
      connect()
    }, delay)
  }, [connect])

  const disconnect = useCallback(() => {
    if (pingTimerRef.current) {
      clearInterval(pingTimerRef.current)
      pingTimerRef.current = null
    }
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = null
    }
    if (wsRef.current) {
      wsRef.current.close()
      wsRef.current = null
    }
    setIsConnected(false)
  }, [])

  const sendMessage = useCallback((message: WSMessage) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message))
    } else {
      console.warn('[WebSocket] Cannot send message: not connected')
    }
  }, [])

  const sendEncryptedMessage = useCallback((recipientId: string, ciphertext: string) => {
    sendMessage({
      type: 'message',
      payload: {
        recipient_id: recipientId,
        ciphertext,
      },
    })
  }, [sendMessage])

  const sendTyping = useCallback((recipientId: string) => {
    sendMessage({
      type: 'typing',
      payload: {
        target_id: recipientId,
      },
    })
  }, [sendMessage])

  const sendReadReceipt = useCallback((recipientId: string, messageIds: string[]) => {
    sendMessage({
      type: 'read',
      payload: {
        target_id: recipientId,
        message_ids: messageIds,
      },
    })
  }, [sendMessage])

  const sendRTCOffer = useCallback((targetId: string, sdp: string, callType?: 'audio' | 'video') => {
    console.debug('[WebSocket] sendRTCOffer called, targetId:', targetId, 'callType:', callType)
    console.debug('[WebSocket] SDP length:', sdp?.length || 0)
    console.debug('[WebSocket] isConnected:', wsRef.current?.readyState === WebSocket.OPEN)
    sendMessage({
      type: 'rtc_offer',
      payload: {
        target_id: targetId,
        data: { sdp, call_type: callType || 'audio' },
      },
    })
  }, [sendMessage])

  const sendRTCAnswer = useCallback((targetId: string, sdp: string) => {
    sendMessage({
      type: 'rtc_answer',
      payload: {
        target_id: targetId,
        data: { sdp },
      },
    })
  }, [sendMessage])

  const sendICECandidate = useCallback((targetId: string, candidate: RTCIceCandidate) => {
    sendMessage({
      type: 'rtc_candidate',
      payload: {
        target_id: targetId,
        data: candidate.toJSON(),
      },
    })
  }, [sendMessage])

  // Connect on mount if token exists
  useEffect(() => {
    if (authToken) {
      connect()
    }

    return () => {
      disconnect()
    }
  }, [authToken, connect, disconnect])

  return {
    isConnected,
    lastMessage,
    sendMessage,
    sendEncryptedMessage,
    sendTyping,
    sendReadReceipt,
    sendRTCOffer,
    sendRTCAnswer,
    sendICECandidate,
    disconnect,
  }
}

// WebSocket message types
export const WSMessageTypes = {
  MESSAGE: 'message',
  TYPING: 'typing',
  READ: 'read',
  DELIVERED: 'delivered',
  RTC_OFFER: 'rtc_offer',
  RTC_ANSWER: 'rtc_answer',
  RTC_CANDIDATE: 'rtc_candidate',
  PRESENCE: 'presence',
  PING: 'ping',
  PONG: 'pong',
} as const
